import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Icon from "../../../components/Icon";
import alert from "../../../assets/images/alert.png";
import SelectComp from "../../../components/Select";
import { Formik } from "formik";
import validationSchema from "./validateSchema";
import { getAllClients } from "../../../api/client";
import Input from "../../../components/Input";
import { getAllExperts, getAllRequerants } from "../../../api/medecin";
import Select from "../../../components/Select";
import Button from "../../../components/ButtonUI";
import {
  addScreeningEvents,
  deleteScreeningEvents,
  getAllCampaigns,
  getAvailableMedecin,
  updateScreeningEvents,
} from "../../../api/screeningEvent";
import makeToast from "../../../components/Snackbar";
import { autocompleteAddress } from "../../../api/geoadresse";
import { IFeature } from "../../../types/IGeocodeAdresse";
import Autocomplete from "../../../components/Autocompleter/Autocomplete";

interface FormValues {
  campaignId: string;
  docReq: string;
  docExpert: string;
  eventDate: Date | null;
  lieu: string;
  numeroFacture: string;
  dateFacture: Date | null;
  tarif: number | null;
  nbJournees?: number;

  // Contact sur place
  contactNom: string;
  contactTelephone: string;
  contactEmail: string;

  // Equipement sur place
  salle: string;
  acces: string;
  autresInformations: string;

  // Suivi logistique
  confirmationClient: boolean;
  confirmationClientDate: Date | null;

  confirmationConsentements: boolean;
  confirmationConsentementsDate: Date | null;

  confirmationRequerant: boolean;
  confirmationRequerantDate: Date | null;

  numeroEnvoiDermatoscope: string;
  dateEnvoiDermatoscope: Date | null;

  numeroRetourDermatoscope: string;
  dateRetourDermatoscope: Date | null;
}

interface Props {
  data: any;
  setShowAgendaModal: Function;
  day: any;
  onSuccess?: () => void;
}
const DayPlan = (props: Props) => {
  const [campaignList, setCampaignList] = useState<any[]>([]);
  const [clientList, setClientList] = useState<any[]>([]);
  const [teleexpertList, setTeleexpertList] = useState<any[]>([]);
  const [requerantList, setRequerantList] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    props.data?.eventDate ? new Date(props.data.eventDate) : null,
  );
  const [allRequerants, setAllRequerants] = useState<any[]>([]);
  const [lieu, setLieu] = useState(props.data?.lieu || "");
  const [locked, setLocked] = useState(!!props.data?.lieu || false);
  const [suggestions, setSuggestions] = useState<IFeature[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  const title = props.data?._id
    ? "Modifier la journée de dépistage"
    : "Nouvelle journée de dépistage";

  const [sections, setSections] = useState({
    contact: false,
    equipement: false,
    logistique: false,
  });

  const toggleSection = (key: keyof typeof sections) => {
    setSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handlesubmitScreeningEvent = async (values) => {
    try {
      const response = await addScreeningEvents(values);
      const newScreeningEvent = response?.data;
      if (!newScreeningEvent) {
        makeToast("error", "Erreur lors de la creation de l'événement");
        return;
      }

      makeToast("success", "Événement créé avec succès.");
      props.onSuccess?.();
      props.setShowAgendaModal("");
    } catch (error) {
      makeToast("error", "Erreur lors de la création de l'événement");
    }
  };
  const handleEditScreeningEvent = async (values) => {
    try {
      const id = props.data._id;
      const response = await updateScreeningEvents(id, values);
      //console.log(response.updated_event)
      if (!response.updated_event) {
        makeToast("error", "Erreur lors de la modification de l'événement");
        return;
      }
      makeToast("success", "Événement modifié avec succès.");
      props.onSuccess?.();
      props.setShowAgendaModal("");
    } catch (error) {
      makeToast("error", "Erreur lors de la modification de l'événement");
    }
  };
  const handleDeleteScreeningEvent = async () => {
    try {
      const id = props.data._id;
      const response = await deleteScreeningEvents(id);
      if (response.message === "deleted") {
        makeToast("success", "Evènement supprimé avec succès.");
        props.onSuccess?.();
      } else {
        makeToast("error", "Erreur lors de la suppression de l'événement");
      }
      props.setShowAgendaModal("");
    } catch (error) {
      makeToast("error", "Erreur lors de la suppression de l'événement");
    }
  };

  const fetchCampaigns = async () => {
    const res = await getAllCampaigns();
    const list = Array.isArray(res) ? res : res.response || res.data || [];

    const options = list.map((c: any) => {
      const isFull = c.usedJournees >= c.nbJournees;

      return {
        value: c._id,
        label: isFull
          ? `${c.clientId?.name} — ${c.usedJournees}/${c.nbJournees} journées (Capacité atteinte)`
          : `${c.clientId?.name} — ${c.usedJournees}/${c.nbJournees} journées`,
        disabled: isFull,
      };
    });

    setCampaignList(options);
  };

  const getToday = () => {
    const d = new Date();
    d.setHours(4, 0, 0, 0);
    return d;
  };

  const initialValues = useMemo(() => {
    return {
      campaignId: props.data?.campaignId?._id || "",
      docReq: props.data?.docReq || "",
      docExpert: props.data?.docExpert || "",
      eventDate: props.data?.eventDate
        ? new Date(props.data.eventDate)
        : getToday(),
      lieu: props.data?.lieu || "",
      numeroFacture: props.data?.numeroFacture || "",
      dateFacture: props.data?.dateFacture
        ? new Date(props.data.dateFacture)
        : getToday(),
      tarif: props.data?.tarif ?? null,
      nbJournees: props.data?.nbJournees ?? null,

      contactNom: props.data?.contactNom || "",
      contactTelephone: props.data?.contactTelephone || "",
      contactEmail: props.data?.contactEmail || "",

      salle: props.data?.salle || "",
      acces: props.data?.acces || "",
      autresInformations: props.data?.autresInformations || "",

      confirmationClient: !!props.data?.confirmationClient,
      confirmationClientDate: props.data?.confirmationClientDate
        ? new Date(props.data.confirmationClientDate)
        : getToday(),

      confirmationConsentements: !!props.data?.confirmationConsentements,
      confirmationConsentementsDate: props.data?.confirmationConsentementsDate
        ? new Date(props.data.confirmationConsentementsDate)
        : getToday(),

      confirmationRequerant: !!props.data?.confirmationRequerant,
      confirmationRequerantDate: props.data?.confirmationRequerantDate
        ? new Date(props.data.confirmationRequerantDate)
        : getToday(),

      numeroEnvoiDermatoscope: props.data?.numeroEnvoiDermatoscope || "",
      dateEnvoiDermatoscope: props.data?.dateEnvoiDermatoscope
        ? new Date(props.data.dateEnvoiDermatoscope)
        : getToday(),

      numeroRetourDermatoscope: props.data?.numeroRetourDermatoscope || "",
      dateRetourDermatoscope: props.data?.dateRetourDermatoscope
        ? new Date(props.data.dateRetourDermatoscope)
        : getToday(),
    };
  }, [props.data?._id]);

  useEffect(() => {
    fetchCampaigns();
  }, []);
  // get clients
  useEffect(() => {
    async function fetchMyAPI() {
      const res = await getAllClients();
      const list = Array.isArray(res) ? res : res.response || res.data || [];
      const options = list.map((client: any) => ({
        label: client.name,
        value: client._id,
      }));
      setClientList(options);
    }

    fetchMyAPI();
  }, []);

  // get all requerants
  useEffect(() => {
    const fetchAll = async () => {
      const res = await getAllRequerants();

      const opts = res.map((r: any) => ({
        label: `${r.firstName} ${r.lastName}`,
        baseLabel: `${r.firstName} ${r.lastName}`,
        value: r._id,
      }));

      setAllRequerants(opts);
    };

    fetchAll();
  }, []);

  //get available requerants
  useEffect(() => {
    if (!selectedDate || !allRequerants.length) {
      setRequerantList(allRequerants);
      return;
    }

    const fetchReq = async () => {
      const dateStr = moment(selectedDate).format("YYYY-MM-DD");
      const available = await getAvailableMedecin(dateStr);
      const availableIds = new Set(available.map((r: any) => r._id));

      const merged = allRequerants.map((r) => {
        const isAvailable = availableIds.has(r.value);

        return {
          ...r,
          disabled: !isAvailable,
          label: isAvailable ? r.baseLabel : `${r.baseLabel} (indisponible)`,
        };
      });

      setRequerantList(merged);
    };

    fetchReq();
  }, [selectedDate, allRequerants]);

  // get expert
  useEffect(() => {
    async function fetchAllExpertsAPI() {
      const listTeleExpert = await getAllExperts();
      setTeleexpertList(listTeleExpert);
      //console.log('listTeleExpert ',listTeleExpert)
    }
    fetchAllExpertsAPI();
  }, []);

  useEffect(() => {
    if (!lieu || lieu.length < 3) {
      setSuggestions([]);
      return;
    }

    const timeout = setTimeout(() => {
      autocompleteAddress(lieu).then(setSuggestions);
    }, 300);

    return () => clearTimeout(timeout);
  }, [lieu]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6 font-medium">
        <div className="w-full max-w-4xl h-full flex flex-col">
          <div className="block w-full rounded-3xl overflow-hidden text-left shadow-2xl bg-white flex-1 flex flex-col">
            <div
              className="flexCenter relative  text-white h-24  py-1"
              style={{ backgroundColor: "#4565f4" }}
            >
              {/* Close modal button */}
              <div className=" absolute right-0  top-0 rounded-bl-3xl border shadow-lg  cursor-pointer -mx-2 -my-2">
                <Icon
                  isIcon={true}
                  style={{ opacity: 0.5 }}
                  name="close"
                  onClick={() => {
                    props.setShowAgendaModal("");
                  }}
                />
              </div>
              <div className="">
                <span className="text-xl font-bold  leading-tight capitalize mx-2 w-2/3">
                  {title}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              <Formik<FormValues>
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={async (values) => {
                  await handlesubmitScreeningEvent(values);
                }}
                validationSchema={validationSchema}
                //validateOnMount={false} // don't validate immediately
                validateOnChange={true} // validate as user types/selects
              >
                {(formik) => {
                  const {
                    values,
                    errors,
                    handleChange,
                    setFieldValue,
                    isSubmitting,
                    isValid,
                    handleSubmit,
                  } = formik;
                  //console.log(isValid)
                  //console.log(values.lieu)
                  //console.log(values.eventDate)
                  //console.log(values.campaignId)
                  return (
                    <form onSubmit={handleSubmit}>
                      <div className="overflow-y-auto max-h-[75vh] bg-gray-50 py-8 rounded-b-3xl">
                        <h3 className="font-semibold text-lg mb-4 px-8">
                          Information générale
                        </h3>
                        <div className="grid grid-cols-2 gap-4 px-8 mt-6 relative">
                          <div className="relative w-full">
                            <span className="absolute top-2 right-4 text-red-500 font-bold pointer-events-none z-10">
                              *
                            </span>
                          {/* journée */}
                          <SelectComp
                            id="campaignId"
                            value={values.campaignId}
                            options={campaignList}
                            onChange={(
                              e: React.ChangeEvent<HTMLSelectElement>,
                            ) => {
                              const value = e.target.value;
                              setFieldValue("campaignId", value);
                              formik.setFieldTouched("campaignId", true, true);
                            }}
                            text="Sélectionner une campagne"
                            className="h-12 px-5 border border-gray-200 w-full"
                          />
                          </div>
                          {/* date */}
                          <div className="p-2 bg-white relative">
                            <label className="text-sm text-gray-400">
                              Date
                            </label>
                            <span className="absolute top-2 right-2 text-red-500 font-bold">
                              *
                            </span>
                            <input
                              type="date"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => e.stopPropagation()}
                              value={
                                values.eventDate
                                  ? moment(values.eventDate).format(
                                      "YYYY-MM-DD",
                                    )
                                  : ""
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                //console.log("Input string:", v);

                                if (!v) {
                                  setFieldValue("eventDate", null);
                                  setSelectedDate(null);
                                  return;
                                }

                                if (v.length === 10) {
                                  const parsedDate = new Date(v);
                                  parsedDate.setHours(4, 0, 0, 0);
                                  //console.log("Safe Date Object:", parsedDate);

                                  setFieldValue("eventDate", parsedDate);
                                  setSelectedDate(parsedDate);
                                }
                              }}
                              onBlur={() =>
                                formik.setFieldTouched("eventDate", true)
                              }
                              className="custom-Input"
                            />
                          </div>
                        </div>
                        {/* Lieu */}
                        <div className="px-8 mt-4">
                          <div className="relative">
                            <span className="absolute top-2 right-3 text-red-500 font-bold pointer-events-none z-10">
                              *
                            </span>
                          <Input
                            id="lieu"
                            name="lieu"
                            value={values.lieu}
                            placeholder="lieu"
                            labelText=""
                            onFocus={() => {
                              if (lieu && lieu.length >= 3) {
                                autocompleteAddress(lieu).then(setSuggestions);
                              }
                            }}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLieu(v);
                              setFieldValue("lieu", e.target.value);

                              setLocked(false);

                              if (v.length >= 3) {
                                autocompleteAddress(v).then(setSuggestions);
                              } else {
                                setSuggestions([]);
                              }
                            }}
                            onBlur={(e) => {
                              formik.handleBlur(e); // Formik tracking restored
                              setTimeout(() => setSuggestions([]), 150);
                            }}
                          />

                            </div>
                          {suggestions.length > 0 && !locked && (
                            <ul className="absolute z-10 w-auto bg-white border border-gray-200 rounded-md shadow">
                              {suggestions.map((feature) => (
                                <li
                                  key={feature.properties.id}
                                  className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                                  onMouseDown={(e) => {
                                    e.preventDefault();

                                    const label = feature.properties.label;

                                    setLieu(label);
                                    setFieldValue("lieu", label);
                                    setSuggestions([]);

                                    setLocked(true);
                                  }}
                                >
                                  {feature.properties.label}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {/* Requérant  */}
                        <div className="grid grid-cols-2 gap-4 px-8 mt-6">
                          <div className="flex flex-col">
                            {props.data?._id && !values.docReq && (
                              <div className="flex items-center gap-1 mb-1 text-orange-600 animate-pulse">
                                <img
                                  src={alert}
                                  alt="warning"
                                  className="w-4 h-4 object-contain"
                                />
                                <span className="text-xs font-bold">
                                  Requérant manquant
                                </span>
                              </div>
                            )}
                            <Autocomplete
                              id="docReq"
                              value={values.docReq}
                              options={requerantList}
                              placeholder="Sélectionner un requérant"
                              className="flex-grow mb-2.5"
                              onChange={(value) => {
                                setFieldValue("docReq", value);
                                formik.setFieldTouched("docReq", true, true);
                              }}
                              onBlur={() => {
                                formik.setFieldTouched("docReq", true);
                              }}
                              error={
                                formik.touched.docReq && formik.errors.docReq
                                  ? formik.errors.docReq
                                  : undefined
                              }
                            />
                          </div>
                          {/* expert  */}
                          <div className="flex flex-col">
                            {props.data?._id && !values.docExpert && (
                              <div className="flex items-center gap-1 mb-1 text-orange-600 animate-pulse">
                                <img
                                  src={alert}
                                  alt="warning"
                                  className="w-4 h-4 object-contain"
                                />
                                <span className="text-xs font-bold">
                                  Expert manquant
                                </span>
                              </div>
                            )}
                            
                            <Autocomplete
                              id="expertId"
                              value={values.docExpert}
                              options={
                                teleexpertList?.map((e) => ({
                                  value: e._id,
                                  label: `${e.lastName} ${e.firstName}`,
                                })) ?? []
                              }
                              placeholder="Sélectionner un expert"
                              className="mb-2"
                              onChange={(value) => {
                                setFieldValue("docExpert", value);
                                formik.setFieldTouched("docExpert", true, true);
                              }}
                              onBlur={() => {
                                formik.setFieldTouched("docExpert", true);
                              }}
                              error={
                                formik.touched.docExpert && formik.errors.docExpert
                                  ? formik.errors.docExpert
                                  : undefined
                              }
                            />
                          </div>
                        </div>
                        {/* Contact sur place */}
                        <div className="px-8 mt-8">
                          <div
                            onClick={() => toggleSection("contact")}
                            className="flex items-center justify-between cursor-pointer px-4 py-3 bg-white border rounded-xl"
                          >
                            <h3 className="font-semibold">Contact sur place</h3>

                            <span className="text-xl">
                              {sections.contact ? "−" : "+"}
                            </span>
                          </div>
                          {sections.contact && (
                            <div className="grid grid-cols-2 gap-4 mt-4">
                              <Input
                                id="contactNom"
                                placeholder="Prénom et Nom"
                                value={values.contactNom}
                                onChange={handleChange}
                                className="custom-Input rounded-xl"
                              />

                              <Input
                                id="contactTelephone"
                                type="tel"
                                placeholder="Téléphone"
                                value={values.contactTelephone}
                                onChange={(e) => {
                                  const v = e.target.value;

                                  if (v === "") {
                                    setFieldValue("contactTelephone", null);
                                    return;
                                  }

                                  if (/^\d+$/.test(v)) {
                                    setFieldValue(
                                      "contactTelephone",
                                      v,
                                    );
                                  }
                                }}
                                className="custom-Input rounded-xl"
                              />

                              <div className="col-span-2">
                                <Input
                                  id="contactEmail"
                                  name="contactEmail"
                                  type="email"
                                  placeholder="Email de contact"
                                  value={values.contactEmail}
                                  onChange={handleChange}
                                  onBlur={formik.handleBlur}
                                />
                              </div>
                              {formik.touched.contactEmail &&
                                formik.errors.contactEmail && (
                                  <div className="text-red-600 text-sm mt-1">
                                    {formik.errors.contactEmail}
                                  </div>
                                )}
                            </div>
                          )}
                        </div>

                        {/* Equipement sur place */}
                        <div className="px-8 mt-8">
                          <div
                            onClick={() => toggleSection("equipement")}
                            className="flex items-center justify-between cursor-pointer px-4 py-3 bg-white border rounded-xl"
                          >
                            <h3 className="font-semibold">
                              Equipement sur place
                            </h3>

                            <span className="text-xl">
                              {sections.equipement ? "−" : "+"}
                            </span>
                          </div>

                          {sections.equipement && (
                            <div className="grid grid-cols-2 gap-4 mt-4">
                              <Input
                                id="salle"
                                placeholder="Salle"
                                value={values.salle}
                                onChange={handleChange}
                                className="custom-Input rounded-xl"
                              />

                              <Input
                                id="acces"
                                placeholder="Accès"
                                value={values.acces}
                                onChange={handleChange}
                                className="custom-Input rounded-xl"
                              />

                              <div className="col-span-2">
                                <textarea
                                  id="autresInformations"
                                  placeholder="Autres informations utiles"
                                  value={values.autresInformations}
                                  onChange={handleChange}
                                  rows={4}
                                  className="w-full border border-gray-200 rounded-xl p-3"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Suivi logistique */}
                        <div className="px-8 mt-8">
                          <div
                            onClick={() => toggleSection("logistique")}
                            className="flex items-center justify-between cursor-pointer px-4 py-3 bg-white border rounded-xl"
                          >
                            <h3 className="font-semibold">Suivi logistique</h3>

                            <span className="text-xl">
                              {sections.logistique ? "−" : "+"}
                            </span>
                          </div>

                          {sections.logistique && (
                            <div className="space-y-4 mt-4">
                              {/* Confirmation client */}
                              <div className="grid grid-cols-2 gap-4 items-center">
                                <div className="flex items-center justify-between">
                                  <span>Confirmation client</span>
                                  <input
                                    key={String(values.confirmationClient)}
                                    type="checkbox"
                                    checked={!!values.confirmationClient}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "confirmationClient",
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                </div>

                                <input
                                  type="date"
                                  value={moment(
                                    values.confirmationClientDate,
                                  ).format("YYYY-MM-DD")}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "confirmationClientDate",
                                      new Date(e.target.value),
                                    )
                                  }
                                  className="border rounded-xl px-3 py-2 w-full"
                                />
                              </div>

                              {/* Confirmation envoi des consentements */}
                              <div className="grid grid-cols-2 gap-4 items-center">
                                <div className="flex items-center justify-between">
                                  <span>
                                    Confirmation envoi des consentements
                                  </span>
                                  <input
                                    key={String(
                                      values.confirmationConsentements,
                                    )}
                                    type="checkbox"
                                    checked={!!values.confirmationConsentements}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "confirmationConsentements",
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                </div>

                                <input
                                  type="date"
                                  value={moment(
                                    values.confirmationConsentementsDate,
                                  ).format("YYYY-MM-DD")}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "confirmationConsentementsDate",
                                      new Date(e.target.value),
                                    )
                                  }
                                  className="border rounded-xl px-3 py-2 w-full"
                                />
                              </div>
                              {/* Confirmation requérant */}
                              <div className="grid grid-cols-2 gap-4 items-center">
                                <div className="flex items-center justify-between">
                                  <span>Confirmation requérant</span>
                                  <input
                                    key={String(values.confirmationRequerant)}
                                    type="checkbox"
                                    checked={!!values.confirmationRequerant}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "confirmationRequerant",
                                        e.target.checked,
                                      );
                                    }}
                                  />
                                </div>

                                <input
                                  type="date"
                                  value={moment(
                                    values.confirmationRequerantDate,
                                  ).format("YYYY-MM-DD")}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "confirmationRequerantDate",
                                      new Date(e.target.value),
                                    )
                                  }
                                  className="border rounded-xl px-3 py-2 w-full"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-4 items-center">
                                <Input
                                  id="numeroEnvoiDermatoscope"
                                  placeholder="Numéro d'envoi du dermatoscope"
                                  value={values.numeroEnvoiDermatoscope ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;

                                    if (v === "") {
                                      setFieldValue(
                                        "numeroEnvoiDermatoscope",
                                        null,
                                      );
                                      return;
                                    }

                                    if (/^\d+$/.test(v)) {
                                      setFieldValue(
                                        "numeroEnvoiDermatoscope",
                                        Number(v),
                                      );
                                    }
                                  }}
                                  className="w-full"
                                />

                                <input
                                  type="date"
                                  value={moment(
                                    values.dateEnvoiDermatoscope,
                                  ).format("YYYY-MM-DD")}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "dateEnvoiDermatoscope",
                                      new Date(e.target.value),
                                    )
                                  }
                                  className="border rounded-xl px-3 py-2 w-full"
                                />
                              </div>

                              {/*  date Confirmation requérant */}
                              <div className="grid grid-cols-2 gap-4 items-center">
                                <Input
                                  id="numeroRetourDermatoscope"
                                  placeholder="Numéro de retour du dermatoscope"
                                  value={values.numeroRetourDermatoscope}
                                  onChange={handleChange}
                                  className="w-full"
                                />

                                <input
                                  type="date"
                                  value={moment(
                                    values.dateRetourDermatoscope,
                                  ).format("YYYY-MM-DD")}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "dateRetourDermatoscope",
                                      new Date(e.target.value),
                                    )
                                  }
                                  className="border rounded-xl px-3 py-2 w-full"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        {showErrors &&
                          Object.entries(errors).map(([key, value]) => (
                            <div
                              key={key}
                              className="text-red-600 mb-1 flex px-8"
                            >
                              • {value}
                            </div>
                          ))}

                        <div className="flex justify-end gap-4 px-8 mt-6">
                          {props.data?._id && (
                            <Button
                              //status="secondary"
                              className="bg-red-600 px-6 py-3 rounded-xl text-lg font-medium"
                              onClick={async () => {
                                await handleDeleteScreeningEvent();
                              }}
                              //disabled={isSubmitting || !isValid}
                            >
                              Supprimer l'événement
                            </Button>
                          )}

                          <Button
                            status="secondary"
                            className="btn px-6 py-3 rounded-xl text-lg font-medium"
                            type="button"
                            onClick={() => {
                              props.setShowAgendaModal("");
                            }}
                          >
                            Annuler
                          </Button>

                          {!props.data?._id && (
                            <Button
                              status="primary"
                              className="btn px-6 py-3 rounded-xl text-lg font-medium"
                              type="submit"
                              disabled={isSubmitting || !isValid}
                              onClick={() => setShowErrors(true)}
                            >
                              Enregistrer
                            </Button>
                          )}

                          {/* UPDATE */}
                          {props.data?._id && (
                            <Button
                              status="primary"
                              className="btn px-6 py-3 rounded-xl text-lg font-medium"
                              type="button"
                              onClick={() =>
                                handleEditScreeningEvent(formik.values)
                              }
                            >
                              Mettre à jour
                            </Button>
                          )}
                        </div>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default DayPlan;
